import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FiMail, FiLock, FiArrowRight, FiShield, FiUser } from 'react-icons/fi';
import logo from '../assets/logo.png';
import icon from '../assets/icon.png';
import slider1 from '../assets/slider-1.jpg';
import slider2 from '../assets/slider-2.jpg';
import slider3 from '../assets/slider-3.jpg';

const SLIDES = [
    { img: slider1, title: 'Eau minérale naturelle', subtitle: 'L’eau de nos racines' },
    { img: slider2, title: 'Ndox mu sell', subtitle: 'Pure, fraîche et 100% sénégalaise' },
    { img: slider3, title: 'Distribution Sama’O', subtitle: 'De Thiès vers tout le Sénégal' },
];

const TEST_ACCOUNTS: { label: string; email: string; password: string; color: string }[] = [
    { label: 'Super Admin', email: 'admin@salsepulse.com', password: 'password', color: '#7C3AED' },
    { label: 'Admin',       email: 'mamadou.diallo@sedima.sn', password: 'password', color: '#2563EB' },
    { label: 'Manager',     email: 'ibrahima.sow@sedima.sn',  password: 'password', color: '#0891B2' },
    { label: 'Superviseur', email: 'abdoulaye.kane@sedima.sn', password: 'password', color: '#059669' },
    { label: 'Commercial',  email: 'moustapha.niang@sedima.sn', password: 'password', color: '#D97706' },
];

const Login: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [slideIdx, setSlideIdx] = useState(0);
    const { login } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const id = setInterval(() => setSlideIdx(i => (i + 1) % SLIDES.length), 5000);
        return () => clearInterval(id);
    }, []);

    const handlePasswordLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(''); setLoading(true);
        try {
            await login(email, password);
            navigate('/admin');
        } catch (err: any) {
            setError(err.response?.data?.error || 'Erreur de connexion');
        } finally { setLoading(false); }
    };

    return (
        <div className="login-page">
            {/* Left panel - branding with SAMAO slider */}
            <div className="login-brand-panel">
                {/* Slider background */}
                <div className="login-slider">
                    {SLIDES.map((s, i) => (
                        <div
                            key={i}
                            className={`login-slide ${i === slideIdx ? 'active' : ''}`}
                            style={{ backgroundImage: `url(${s.img})` }}
                        />
                    ))}
                    <div className="login-slider-overlay" />
                </div>

                {/* Content over slider */}
                <div className="login-brand-content">
                    <img src={logo} alt="SAMAO" className="login-brand-logo" />
                    <p className="login-brand-tagline">
                        L’eau de nos racines · Distribution Sama’O
                    </p>
                    <div className="login-slide-caption">
                        <div className="login-slide-title">{SLIDES[slideIdx].title}</div>
                        <div className="login-slide-subtitle">{SLIDES[slideIdx].subtitle}</div>
                    </div>
                    <div className="login-slider-dots">
                        {SLIDES.map((_, i) => (
                            <button
                                key={i}
                                type="button"
                                aria-label={`Aller à la slide ${i + 1}`}
                                className={`login-slider-dot ${i === slideIdx ? 'active' : ''}`}
                                onClick={() => setSlideIdx(i)}
                            />
                        ))}
                    </div>
                </div>
                <div className="login-brand-footer">
                    SAMAO SMART SALES v1.0
                </div>
            </div>

            {/* Right panel - form */}
            <div className="login-form-panel">
                <div className="login-card">
                    <div className="login-card-header">
                        <img src={icon} alt="" className="login-card-icon-mobile" />
                        <h1>Bienvenue</h1>
                        <p>Connectez-vous a votre espace</p>
                    </div>

                    {error && (
                        <div className="login-alert login-alert-error">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/><path d="M8 5v3.5M8 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                            {error}
                        </div>
                    )}

                    <form onSubmit={handlePasswordLogin} className="login-form-inner">
                        <div className="login-field">
                            <label>Adresse email</label>
                            <div className="login-input-wrap">
                                <FiMail className="login-input-icon" />
                                <input
                                    type="email" value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    placeholder="votre@email.com" required autoFocus
                                />
                            </div>
                        </div>
                        <div className="login-field">
                            <label>Mot de passe</label>
                            <div className="login-input-wrap">
                                <FiLock className="login-input-icon" />
                                <input
                                    type="password" value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    placeholder="Votre mot de passe" required
                                />
                            </div>
                        </div>
                        <button type="submit" className="login-submit" disabled={loading}>
                            {loading ? (
                                <span className="login-spinner" />
                            ) : (
                                <>Se connecter <FiArrowRight size={18} /></>
                            )}
                        </button>
                    </form>

                    <div className="login-test-accounts">
                        <div className="login-test-accounts-label">
                            <FiUser size={11} /> Comptes de test (cliquez pour remplir)
                        </div>
                        <div className="login-test-accounts-list">
                            {TEST_ACCOUNTS.map(acc => (
                                <button
                                    key={acc.email}
                                    type="button"
                                    className="login-test-account"
                                    onClick={() => {
                                        setEmail(acc.email);
                                        setPassword(acc.password);
                                        setError('');
                                    }}
                                    style={{ ['--accent' as any]: acc.color }}
                                    title={acc.email}
                                >
                                    <span className="login-test-account-dot" />
                                    <span className="login-test-account-label">{acc.label}</span>
                                    <span className="login-test-account-email">{acc.email}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="login-card-footer">
                        <FiShield size={12} /> Connexion securisee
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
